/* eslint-disable */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { ObservableErrorResourceParser } from '../../util/errors';
import { FieldValidationError, PagingRequest, QueryResult, ResourceQueryResult, Services } from '../../util/services';
import { List, Map as ImmutableMap, Set } from 'immutable';
import { OffsetDateTime } from '../../util/dates';
import { DisableRequest, IdentityMessage } from '../../util/messages';
import { DqlQuery, Query } from '../../query/field';
import { FilterField } from '../../query/filterfields';
import { OrderField } from '../../query/orderfields';
import { LedgerNumberResource, LedgerNumberResourceService } from './ledger-number-resource.service';
import {Cacheable} from "ts-cacheable";
import {UiConstants} from "../../../util/core-utils";
import {DownloadedFile} from "../../util/downloaded-files";
import {MasterDataRecordResource} from "../../masterdata/master-data-record-resource.service";
import {MasterDataRecord} from "../../masterdata/master-data-record.service";

/* eslint-enable */

@Injectable()
export class LedgerNumberService implements LedgerNumber.Service {

  private readonly filterField = new FilterField.LedgerNumber();
  private readonly orderField = new OrderField.LedgerNumber();
  private readonly fields = new LedgerNumber.Fields.LedgerNumber();

  constructor(private resourceService: LedgerNumberResourceService) {
  }

  query(request: LedgerNumber.QueryRequest): Observable<QueryResult<LedgerNumber.LedgerNumber>> {
    return Observable.create((observer: Observer<QueryResult<LedgerNumber.LedgerNumber>>) => {
      const resourceRequest = LedgerNumber.toResourceQueryRequest(this.filterField, this.orderField, this.fields, request);
      return this.resourceService.query(resourceRequest).subscribe(
        (result: ResourceQueryResult<LedgerNumberResource.LedgerNumber>) => {
          observer.next({
            items: List.of(...result.items.map((item) => LedgerNumber.toPublic(item))),
            pagingResult: result.pagingResult
          });
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  @Cacheable({
    maxAge: UiConstants.defaultCacheMaxAge,
    slidingExpiration: true
  })
  get(request: LedgerNumber.GetRequest): Observable<LedgerNumber.LedgerNumber> {
    return Observable.create((observer: Observer<LedgerNumber.LedgerNumber>) => {
      const resourceRequest = LedgerNumber.toResourceGetRequest(this.fields, request);
      return this.resourceService.get(resourceRequest).subscribe(
        (result: LedgerNumberResource.LedgerNumber) => {
          observer.next(LedgerNumber.toPublic(result));
        },
        (error: Error) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  create(request: LedgerNumber.CreateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = LedgerNumber.toResourceCreateRequest(request);
      return this.resourceService.create(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  update(request: LedgerNumber.UpdateRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = LedgerNumber.toResourceUpdateRequest(request);
      return this.resourceService.update(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  disable(request: DisableRequest): Observable<IdentityMessage> {
    return Observable.create((observer: Observer<IdentityMessage>) => {
      const resourceRequest = request;
      return this.resourceService.disable(resourceRequest).subscribe(
        (result: IdentityMessage) => {
          observer.next(result);
        },
        (error: any) => {
          observer.error(this.translateError(error));
        },
        () => {
          observer.complete();
        });
    });
  }

  exportXls(request: LedgerNumber.QueryRequest): Observable<DownloadedFile> {
    const resourceRequest: LedgerNumberResource.QueryRequest = LedgerNumber.toResourceQueryRequest(this.filterField, this.orderField, this.fields, request);
    return this.resourceService.exportXls(resourceRequest);
  }

  exportXlsTemplate(): Observable<DownloadedFile> {
    return this.resourceService.exportXlsTemplate();
  }


  private translateError(error: any): any {
    const res = ObservableErrorResourceParser.parseError(error);
    const fieldErrors = ObservableErrorResourceParser.extractFieldErrors(res);
    const fieldErrorMap = ObservableErrorResourceParser.toFieldErrorMap(Keys.toValidatedField, fieldErrors);
    if (!fieldErrorMap.isEmpty()) {
      return FieldValidationError.of(fieldErrorMap);
    }
    return error;
  }
}

export namespace LedgerNumber {


  export interface Service {

    // <editor-fold desc="CRUD">

    query(request: QueryRequest): Observable<QueryResult<LedgerNumber>>;

    get(request: GetRequest): Observable<LedgerNumber>;

    create(request: CreateRequest): Observable<IdentityMessage>;

    // </editor-fold>

  }

  // <editor-fold desc="CRUD">

  export interface QueryRequest {
    fields?: Query.FieldFunction<Fields.LedgerNumber>;
    filter?: Query.FilterFunction<FilterField.LedgerNumber>;
    order?: Query.OrderFunction<OrderField.LedgerNumber>;
    paging?: PagingRequest;
    rights?: Set<string>;
    noProgressBar?: boolean;
  }

  export interface GetRequest {
    id: number;
    fields?: Query.FieldFunction<Fields.LedgerNumber>;
    rights?: Set<string>;
  }

  export interface LedgerNumber {
    id: number;
    grantedRights: Set<string>;
    creationTime: OffsetDateTime;
    updateTime: OffsetDateTime;
    ledgerNumber: string;
    name?: string;
    disabled: boolean;
  }

  export interface CreateRequest {
    ledgerNumber: string;
    name?: string;
  }

  export interface UpdateRequest {
    id: number;
    ledgerNumber: string;
    name?: string;
  }

  export function toResourceQueryRequest(
    filterField: FilterField.LedgerNumber,
    orderField: OrderField.LedgerNumber,
    fields: LedgerNumber.Fields.LedgerNumber,
    request: QueryRequest): LedgerNumberResource.QueryRequest {
    const filter: string | undefined = DqlQuery.toOptionalFilter(filterField, request.filter);
    const order: string | undefined = DqlQuery.toOptionalOrder(orderField, request.order);
    const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
    return {
      filter: filter,
      order: order,
      fields: field,
      page_number: request.paging ? request.paging.pageNumber : undefined,
      number_of_items: request.paging ? request.paging.numberOfItems : undefined,
      rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights),
      no_progress_bar: request.noProgressBar
    };
  }

  export function toResourceGetRequest(
    fields: LedgerNumber.Fields.LedgerNumber,
    request: GetRequest): LedgerNumberResource.GetRequest {
    const field: string | undefined = DqlQuery.toOptionalFields(fields, request.fields);
    return {
      id: request.id,
      fields: field,
      rights: DqlQuery.RightRequestSerializer.getInstance().serialize(request.rights)
    };
  }

  export function toResourceCreateRequest(request: CreateRequest): LedgerNumberResource.CreateRequest {
    return {
      ledger_number: request.ledgerNumber,
      name: request.name,
    };
  }

  export function toResourceUpdateRequest(request: UpdateRequest): LedgerNumberResource.UpdateRequest {
    return {
      id: request.id,
      ledger_number: request.ledgerNumber,
      name: request.name,
    };
  }


  export function toPublic(r: LedgerNumberResource.LedgerNumber): LedgerNumber.LedgerNumber {
    return {
      id: r.id,
      grantedRights: r.granted_rights ? Set.of(...r.granted_rights) : Set.of(),
      creationTime: Services.toOffsetDateTime(r.creation_time),
      updateTime: Services.toOffsetDateTime(r.update_time),
      ledgerNumber: r.ledger_number,
      name: r.name,
      disabled: r.disabled
    };
  }

  export enum ValidatedField {
    UNKNOWN,
    NAME,
    LEDGER_NUMBER
  }

  export namespace Fields {

    export class LedgerNumber {

      readonly id: Query.Field = new DqlQuery.Field('id');
      readonly grantedRights: Query.Field = new DqlQuery.Field('granted_rights');
      readonly creationTime: Query.Field = new DqlQuery.Field('creation_time');
      readonly updateTime: Query.Field = new DqlQuery.Field('update_time');
      readonly ledgerNumber: Query.Field = new DqlQuery.Field('ledger_number');
      readonly name: Query.Field = new DqlQuery.Field('name');
      readonly disabled: Query.Field = new DqlQuery.Field('disabled');

      get each(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.creationTime,
          this.updateTime,
          this.ledgerNumber,
          this.name,
          this.disabled,
        );
      }

      get forList(): Set<Query.Field> {
        return Set.of(
          this.id,
          this.name,
          this.ledgerNumber,
          this.disabled,
          this.creationTime,
        );
      }

      get multiselect(): Set<Query.Field> {
        return Set.of(this.id, this.name, this.ledgerNumber, this.disabled);
      }
    }

  }

}

// <editor-fold desc="Internal">


class Keys {

  private static readonly NAME = 'name';
  private static readonly LEDGER_NUMBER = 'ledger_number';

  private static readonly keyValidatedFieldMap: ImmutableMap<string, LedgerNumber.ValidatedField> = ImmutableMap.of(
    Keys.NAME, LedgerNumber.ValidatedField.NAME,
    Keys.LEDGER_NUMBER, LedgerNumber.ValidatedField.LEDGER_NUMBER,
  );

  public static toValidatedField(fieldKey: string): LedgerNumber.ValidatedField {
    return Keys.keyValidatedFieldMap.get(fieldKey, LedgerNumber.ValidatedField.UNKNOWN);
  }

}

// </editor-fold>


